import { useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { getApiHeaders, API_TYPES } from '../utils/apiHeaders';

const API_BASE_URL = import.meta.env.VITE_DB_API_URL || '/api';


export const useAdminManagement = (subscriptionData, onSuccess) => {

const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const [addAdminProgress, setAddAdminProgress] = useState(null);
const [deleteAdminProgress, setDeleteAdminProgress] = useState(null);
const { getAccessToken, getUserEmail } = useAuth();

  // Get current admin's ID from subscription data
  const getCurrentAdminId = useCallback(() => {
    const currentUserEmail = getUserEmail();
    const currentAdmin = subscriptionData?.admins?.find(admin => 
      admin.email === currentUserEmail || admin.Email === currentUserEmail
    );
    return currentAdmin?.adminID || currentAdmin?.AdminID;
  }, [subscriptionData, getUserEmail]);

  // Get list of admins for the current subscription
  const getAdmins = useCallback(async () => {
    const adminId = getCurrentAdminId();
    if (!adminId) {
      throw new Error('Unable to determine current admin ID');
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/GetAdmins?adminId=${adminId}`, {
        method: 'GET',
        headers: getApiHeaders(token, API_TYPES.DATABASE)
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to get admins: ${errorText}`);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getCurrentAdminId, getAccessToken]);

  // Add a new admin (fullName is now required)
  const addAdmin = useCallback(async (email, fullName) => {
    const adminId = getCurrentAdminId();
    if (!adminId) {
      throw new Error('Unable to determine current admin ID');
    }

    if (!fullName || !fullName.trim()) {
      throw new Error('Display Name is required');
    }

    setLoading(true);
    setError(null);
    setAddAdminProgress({
      database: 'pending',
      complete: 'pending'
    });

    try {
      const token = await getAccessToken();
      
      setAddAdminProgress({
        database: 'in-progress',
        complete: 'pending'
      });
      
      // Prepare request body without OIDC claims (will be populated on first login)
      const requestBody = {
        email: email,
        fullName: fullName
        // identityProvider and subjectId will be null initially
        // They will be populated when the admin first logs in
      };
      
      const response = await fetch(`${API_BASE_URL}/CreateAdmin?adminId=${adminId}`, {
        method: 'POST',
        headers: getApiHeaders(token, API_TYPES.DATABASE),
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        
        setAddAdminProgress({
          database: 'completed',
          complete: 'in-progress'
        });

        // Small delay to show completion state
        await new Promise(resolve => setTimeout(resolve, 500));

        setAddAdminProgress({
          database: 'completed',
          complete: 'completed'
        });

        // Callback removed - let component handle success silently
        return result;
      } else {
        const errorText = await response.text();
        setAddAdminProgress({
          database: 'error',
          complete: 'pending'
        });
        throw new Error(errorText);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
      // Clear progress after a delay
      setTimeout(() => setAddAdminProgress(null), 1000);
    }
  }, [getCurrentAdminId, getAccessToken, onSuccess]);


  // Delete an admin
  const deleteAdmin = useCallback(async (adminIdToDelete) => {
    const currentAdminId = getCurrentAdminId();
    if (!currentAdminId) {
      throw new Error('Unable to determine current admin ID');
    }

    // Prevent self-deletion
    if (currentAdminId === adminIdToDelete) {
      throw new Error('You cannot delete yourself. Have another admin remove your access.');
    }

    setLoading(true);
    setError(null);
    setDeleteAdminProgress({
      database: 'pending',
      complete: 'pending'
    });

    try {
      const token = await getAccessToken();
      
      setDeleteAdminProgress({
        database: 'in-progress',
        complete: 'pending'
      });
      
      const response = await fetch(`${API_BASE_URL}/DeleteAdmin?adminId=${currentAdminId}`, {
        method: 'DELETE',
        headers: getApiHeaders(token, API_TYPES.DATABASE),
        body: JSON.stringify({
          adminID: adminIdToDelete
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        setDeleteAdminProgress({
          database: 'completed',
          complete: 'in-progress'
        });

        // Small delay to show completion state
        await new Promise(resolve => setTimeout(resolve, 500));

        setDeleteAdminProgress({
          database: 'completed',
          complete: 'completed'
        });

        // Callback removed - let component handle success silently
        return result;
      } else {
        const errorText = await response.text();
        setDeleteAdminProgress({
          database: 'error',
          complete: 'pending'
        });
        throw new Error(`Failed to delete admin: ${errorText}`);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
      // Clear progress after a delay
      setTimeout(() => setDeleteAdminProgress(null), 1000);
    }
  }, [getCurrentAdminId, getAccessToken, onSuccess]);

  return {
    loading,
    error,
    addAdminProgress,
    deleteAdminProgress,
    getAdmins,
    addAdmin,
    deleteAdmin,
    currentAdminId: getCurrentAdminId()
  };
};